import { screenToStudio } from "@logic/lib/design"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import type { TransactionOperation } from ".."
import { moveBoxPositionState, zoomState } from "../../../view"
import { findNodeState, nodeIndex } from "../../nodes"
import { findPanelState, panelIndex } from "../../panels"
import type {
  NodeActionTo_,
  PanelActionTo_,
  UnknownActionForUnknownElement,
  UnknownChangeForUnknownElement,
} from "../../timeline"
import {
  divergeTimeline,
  generateElementChange,
} from "../../timeline/transactions"

export const moveAll: TransactionOperation = (transactors) => {
  const { get, set } = transactors
  const zoom = get(zoomState)
  const moveBoxPositionInStudio = screenToStudio.point(
    get(moveBoxPositionState),
    zoom,
  )

  const nodeIds = get(nodeIndex)
  const nodeActions: UnknownActionForUnknownElement[] = nodeIds.map(
    (id): NodeActionTo_[`UPDATE`] => ({
      type: `UPDATE`,
      family: `NODE`,
      id,
      payload: get(findNodeState(id)),
    }),
  )

  const panelIds = get(panelIndex)
  const panelActions: UnknownActionForUnknownElement[] = panelIds.map(
    (id): PanelActionTo_[`UPDATE`] => ({
      type: `UPDATE`,
      family: `PANEL`,
      id,
      payload: get(findPanelState(id)),
    }),
  )

  const newActions = [...nodeActions, ...panelActions]

  nodeIds.forEach((id) => {
    set(findNodeState(id), (current) => ({
      x: current.x + moveBoxPositionInStudio.x,
      y: current.y + moveBoxPositionInStudio.y,
    }))
  })

  panelIds.forEach((id) => {
    set(findPanelState(id), (current) => ({
      ...current,
      x: current.x + moveBoxPositionInStudio.x,
      y: current.y + moveBoxPositionInStudio.y,
    }))
  })

  const changes: (UnknownChangeForUnknownElement | undefined)[] = newActions.map(
    (action): UnknownChangeForUnknownElement =>
      generateElementChange(transactors, { action, from: `undo` }),
  )

  divergeTimeline(transactors, changes)

  set(moveBoxPositionState, { x: 0, y: 0 })
}

export const useMoveAll = (): VoidFunction =>
  useRecoilTransaction_UNSTABLE((transactors) => () => moveAll(transactors))
